import { useRef, useState } from 'react';

import { type EloIconType } from '@plurall/elo-v3';

import { useAuthentication } from '@/hooks/queries/authentication/useAuthentication';
import { usePerformLogout } from '@/hooks/queries/authentication/usePerformLogout';
import { resolvePath } from '@/lib/formatters/resolveEnvUrl';

import { createMenuContentKeyHandler, createTriggerKeyHandler } from '../shared/menuKeyboardNav';

export type ProfileMenuActionItem = {
  value: string;
  icon: EloIconType;
  label: string;
  path?: string;
  isLogout?: boolean;
  visible?: (context: { isAluno: boolean; isProfessor: boolean }) => boolean;
};

type UseProfileMenuResult = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  hoveredItem: string | null;
  setHoveredItem: React.Dispatch<React.SetStateAction<string | null>>;
  contentRef: React.RefObject<HTMLDivElement | null>;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  openedFromKeyboardRef: React.RefObject<boolean>;
  firstName: string;
  initial: string;
  visibleItems: ProfileMenuActionItem[];
  handleSelect: (item: ProfileMenuActionItem) => Promise<void>;
  handleTriggerKeyDown: (e: React.KeyboardEvent) => void;
  handleContentKeyDown: (e: React.KeyboardEvent) => void;
};

const PROFILE_MENU_ITEMS: ProfileMenuActionItem[] = [
  { value: 'profiles-hub', icon: 'face-like-square', label: 'Meus dados', path: 'cadastro/perfil' },
  {
    value: 'diagnostic-questionnaire',
    icon: 'chart-pie',
    label: 'Avaliação diagnóstica',
    path: 'avaliacao/diagnostica',
    visible: ({ isAluno }) => isAluno,
  },
  {
    value: 'use-terms',
    icon: 'book-open',
    label: 'Termos de uso',
    path: 'cadastro/corretor/termos-de-uso',
    visible: ({ isProfessor }) => isProfessor,
  },
  {
    value: 'logout',
    icon: 'sign-out',
    label: 'Sair',
    isLogout: true,
  },
];

function getFirstName(nome: string | undefined): string {
  return nome?.trim()?.split(' ')?.[0] ?? '';
}

function getPreferredGreetingSource(apelido: string | undefined, nome: string | undefined): string | undefined {
  const social = apelido?.trim();
  if (social) return social;
  const legal = nome?.trim();
  return legal || undefined;
}

export function useProfileMenu(): UseProfileMenuResult {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const openedFromKeyboardRef = useRef(false);

  const { data: user } = useAuthentication();
  const performLogout = usePerformLogout();

  const profile = user?.perfil?.toLowerCase().trim() ?? '';
  const isAluno = profile.includes('aluno');
  const isProfessor = profile.includes('professor');
  const visibleItems = PROFILE_MENU_ITEMS.filter((item) => item.visible?.({ isAluno, isProfessor }) ?? true);

  const greetingSource = getPreferredGreetingSource(user?.apelido, user?.nome);
  const firstName = getFirstName(greetingSource);
  const initial = (greetingSource?.charAt(0) ?? user?.nome?.charAt(0) ?? '?').toUpperCase();

  const closeMenu = (): void => setIsOpen(false);

  const handleSelect = async (item: ProfileMenuActionItem): Promise<void> => {
    closeMenu();
    if (item.isLogout) {
      await performLogout();
      return;
    }
    if (item.path) {
      window.location.href = resolvePath(item.path);
    }
  };

  const triggerTabHandler = createTriggerKeyHandler({ isOpen, contentRef });
  const handleTriggerKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      openedFromKeyboardRef.current = true;
      e.preventDefault();
      e.stopPropagation();
      setIsOpen((prev) => !prev);
      return;
    }
    triggerTabHandler(e);
  };

  const handleContentKeyDown = createMenuContentKeyHandler({ triggerRef, closeMenu }) as (
    e: React.KeyboardEvent
  ) => void;

  return {
    isOpen,
    setIsOpen,
    hoveredItem,
    setHoveredItem,
    contentRef,
    triggerRef,
    openedFromKeyboardRef,
    firstName,
    initial,
    visibleItems,
    handleSelect,
    handleTriggerKeyDown,
    handleContentKeyDown,
  };
}
